import styled from 'styled-components'
import { SearchInput } from '../../../ui/SearchInput/SearchInput'
import { AccentButton } from '../../../ui/Button/AccentButton'
import KbdChipButton from '../../../ui/KbdChipButton/KbdChipButton'
import ExcelFileIcon from '../../../icons/IconLib/FileExcelIcon'
import FilterIcon from '../../../icons/IconLib/FilterIcon'
import UploadCloudIcon from '../../../icons/IconLib/UploadCloudIcon'

export interface TableToolbarProps {
  searchValue: string
  onSearchChange: (value: string) => void
  searchPlaceholder?: string
  /** Excel export chip (rendered only when provided). */
  onExport?: () => void
  /** Opens the module's file-import UI (chip rendered only when provided). */
  onImport?: () => void
  importLabel?: string
  /** Opens the module's filter UI (chip rendered only when provided). */
  onFilter?: () => void
  /** Active-filter counter badge on the filter chip. */
  filterCount?: number
  onAdd?: () => void
  addLabel?: string
}

/**
 * Standard table-module toolbar actions: a flexible search field, optional Excel
 * export / file-import chips, an optional filter chip, and an optional accent
 * "add" button (collapses to an icon-only square on mobile). Drop into
 * `GridTable`'s `toolbarActions` slot.
 */
export const TableToolbar = ({
  searchValue,
  onSearchChange,
  searchPlaceholder,
  onExport,
  onImport,
  importLabel,
  onFilter,
  filterCount,
  onAdd,
  addLabel,
}: TableToolbarProps) => (
  <Actions>
    <Search
      value={searchValue}
      onChange={(e) => onSearchChange(e.target.value)}
      placeholder={searchPlaceholder}
    />
    {onExport && (
      <KbdChipButton onClick={onExport} aria-label="ייצוא לאקסל">
        <ExcelFileIcon />
      </KbdChipButton>
    )}
    {onImport && (
      <KbdChipButton onClick={onImport} aria-label={importLabel ?? 'ייבוא קובץ'}>
        <UploadCloudIcon size={16} />
      </KbdChipButton>
    )}
    {onFilter && (
      <KbdChipButton onClick={onFilter} count={filterCount} aria-label="סינון">
        <FilterIcon size={16} />
      </KbdChipButton>
    )}
    {onAdd && (
      <AddButton variant="accent" fullWidth={false} onClick={onAdd} iconRight={PlusIcon}>
        {addLabel}
      </AddButton>
    )}
  </Actions>
)

export default TableToolbar

const PlusIcon = (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 5v14M5 12h14" />
  </svg>
)

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  /* Buttons keep their size; only the search flexes. */
  & > button {
    flex-shrink: 0;
  }
`

const Search = styled(SearchInput)`
  width: 100%;
  min-height: 2.5rem;
`

const AddButton = styled(AccentButton)`
  height: 2.5rem;
  padding: 0 0.875rem;
  font-size: ${({ theme }) => theme.text.md};

  /* Mobile: collapse to an icon-only (+) square button. */
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 2.5rem;
    padding: 0;

    & > span:first-child {
      display: none;
    }
  }
`
